import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Users,
  MessageCircle,
  Send,
  Loader2,
  Sparkles,
  LogOut,
  Lock,
  KeyRound,
  FileText,
  CalendarDays,
  Phone,
  Map,
  Upload,
  Download,
  Trash2,
  Syringe,
  X,
  Pill,
  Check,
  Plus,
  Clock,
  BookOpen,
  Languages,
} from "lucide-react";
import { VoiceRecorderButton, VoiceMessagePlayer } from "../components/VoiceMessage";
import NotificationBell from "../components/NotificationBell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "soarlife-client-token";
const USER_KEY = "soarlife-client-user";

function useClient() {
  const [client, setClient] = useState(() => {
    try {
      const u = localStorage.getItem(USER_KEY);
      return u ? JSON.parse(u) : null;
    } catch {
      return null;
    }
  });
  const token = () => localStorage.getItem(TOKEN_KEY);
  const authHeader = () => {
    const t = token();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };
  // axios config that works for BOTH google-cookie clients AND bearer-token clients
  const authAxios = () => ({ headers: authHeader(), withCredentials: true });
  const setAuth = (data) => {
    if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.client));
    setClient(data.client);
  };
  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setClient(null);
  };
  return { client, setAuth, logout, authHeader, authAxios, setClient };
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function Community() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { client, setAuth, logout, authHeader, setClient } = useClient();
  const processedHash = useRef(false);

  // Deep-link support: /portal#my-medications scrolls to the medications section
  useEffect(() => {
    if (location.hash === "#my-medications" && client?.access_granted) {
      const timer = setTimeout(() => {
        document.getElementById("my-medications")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [location.hash, client?.access_granted]);

  // Handle Google Auth callback (session_id in URL fragment)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    const hash = location.hash || "";
    if (!hash.includes("session_id=")) return;
    if (processedHash.current) return;
    processedHash.current = true;
    const sessionId = new URLSearchParams(hash.slice(1)).get("session_id");
    if (!sessionId) return;
    (async () => {
      try {
        const res = await axios.post(
          `${API}/auth/google-session`,
          {},
          { headers: { "X-Session-ID": sessionId }, withCredentials: true }
        );
        setAuth({ client: res.data.client }); // no bearer token — cookie is set
        // Clean the hash
        window.history.replaceState(null, "", "/community");
      } catch (e) {
        window.history.replaceState(null, "", "/community");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  // Refresh profile so access_granted stays in sync (e.g. activated on another device)
  useEffect(() => {
    if (!client) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/clients/me`, {
          headers: authHeader(),
          withCredentials: true,
        });
        if (res.data && res.data.access_granted && !client.access_granted) {
          const merged = { ...client, ...res.data };
          localStorage.setItem(USER_KEY, JSON.stringify(merged));
          setClient(merged);
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);

  if (!client) return <AuthScreen onAuth={setAuth} />;
  if (!client.access_granted)
    return (
      <AccessCodeScreen
        client={client}
        authHeader={authHeader}
        onActivated={(c) => setAuth({ client: c })}
        onLogout={logout}
      />
    );
  if (groupId) return <GroupRoom groupId={groupId} client={client} authHeader={authHeader} onLogout={logout} />;
  return <GroupList client={client} authHeader={authHeader} onLogout={logout} onOpen={(id) => navigate(`/community/${id}`)} />;
}

function AccessCodeScreen({ client, authHeader, onActivated, onLogout }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subErr, setSubErr] = useState("");

  const subscribe = async () => {
    if (subscribing) return;
    setSubscribing(true);
    setSubErr("");
    try {
      const res = await axios.post(
        `${API}/payments/checkout`,
        { origin_url: window.location.origin },
        { headers: authHeader(), withCredentials: true }
      );
      window.location.href = res.data.checkout_url;
    } catch (e2) {
      setSubErr(e2?.response?.data?.detail || "Could not start checkout. Please try again.");
      setSubscribing(false);
    }
  };

  const activate = async (e) => {
    e.preventDefault();
    if (!code.trim() || loading) return;
    setLoading(true);
    setErr("");
    try {
      await axios.post(
        `${API}/clients/activate-code`,
        { code: code.trim() },
        { headers: authHeader(), withCredentials: true }
      );
      onActivated({ ...client, access_granted: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#3F5043] flex items-center justify-center p-5">
      <div
        data-testid="access-code-screen"
        className="w-full max-w-sm bg-[#F8F7F3] rounded-3xl p-8 shadow-2xl"
      >
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[#3F5043] flex items-center justify-center mb-4">
            <KeyRound size={20} className="text-[#AE8F78]" />
          </div>
          <div className="font-serif italic text-3xl text-[#3F5043] leading-tight text-center">
            Almost there, {client.full_name.split(" ")[0]}
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mt-1 text-center">
            Enter your client access code
          </div>
        </div>
        <p className="text-sm text-[#3F5043]/70 leading-relaxed mb-5 text-center">
          Soarlife AI, community groups, and secure record uploads unlock with the
          personal code your Soarlife nurse gave you after becoming a client.
        </p>
        <form onSubmit={activate}>
          <input
            data-testid="access-code-input"
            required
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="SOAR-XXXXXX"
            className="w-full bg-[#EDEDEE] rounded-2xl px-4 py-3 text-center text-lg font-mono tracking-[0.2em] text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#899480]"
          />
          {err && (
            <div
              data-testid="access-code-error"
              className="mt-3 text-sm text-[#875F45] bg-[#875F45]/10 border border-[#875F45]/30 rounded-xl px-3 py-2"
            >
              {err}
            </div>
          )}
          <button
            type="submit"
            data-testid="access-code-submit"
            disabled={loading}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Unlock my portal"}
          </button>
        </form>
        <div className="mt-6 pt-6 border-t border-[#3F5043]/10">
          <div className="text-center text-[10px] uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">
            No code yet? Become a member
          </div>
          <div className="bg-white/70 border border-[#3F5043]/10 rounded-2xl p-4 mb-3">
            <div className="flex items-baseline justify-center gap-1 mb-2">
              <span className="font-serif text-3xl text-[#3F5043]">$59.99</span>
              <span className="text-xs text-[#3F5043]/60">/month · cancel anytime</span>
            </div>
            <ul className="text-xs text-[#3F5043]/70 space-y-1.5 text-left">
              <li className="flex gap-2"><Sparkles size={12} className="text-[#875F45] mt-0.5 shrink-0" /> Soarlife AI Fertility Concierge, day or night</li>
              <li className="flex gap-2"><Sparkles size={12} className="text-[#875F45] mt-0.5 shrink-0" /> Your personal Fertility Roadmap with journey score</li>
              <li className="flex gap-2"><Sparkles size={12} className="text-[#875F45] mt-0.5 shrink-0" /> Secure records vault &amp; direct nurse messaging</li>
              <li className="flex gap-2"><Sparkles size={12} className="text-[#875F45] mt-0.5 shrink-0" /> Private community groups with others on the journey</li>
            </ul>
          </div>
          <button
            type="button"
            onClick={subscribe}
            disabled={subscribing}
            data-testid="subscribe-button"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#875F45] text-[#F8F7F3] text-sm font-medium hover:bg-[#3F5043] transition-colors disabled:opacity-60"
          >
            {subscribing ? <Loader2 size={16} className="animate-spin" /> : "Subscribe & unlock instantly"}
          </button>
          {subErr && (
            <div
              data-testid="subscribe-error"
              className="mt-3 text-sm text-[#875F45] bg-[#875F45]/10 border border-[#875F45]/30 rounded-xl px-3 py-2 text-center"
            >
              {subErr}
            </div>
          )}
          <div className="mt-2 text-center text-[10px] text-[#3F5043]/50">
            Secure checkout by Stripe · unlocks immediately after payment
          </div>
        </div>
        <div className="mt-5 text-center text-xs text-[#3F5043]/60 leading-relaxed">
          Not a client yet?{" "}
          <a
            href="/book"
            className="text-[#875F45] underline"
          >
            Book a consultation
          </a>{" "}
          or text (844) 663-2125.
        </div>
        <button
          onClick={onLogout}
          data-testid="access-code-logout"
          className="mt-4 w-full text-center text-xs uppercase tracking-[0.2em] text-[#899480] hover:text-[#3F5043]"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const url = mode === "login" ? `${API}/clients/login` : `${API}/clients/register`;
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const res = await axios.post(url, payload);
      onAuth(res.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#3F5043] flex items-center justify-center p-5">
      <form
        onSubmit={submit}
        data-testid="community-auth-form"
        className="w-full max-w-sm bg-[#F8F7F3] rounded-3xl p-8 shadow-2xl"
      >
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[#3F5043] flex items-center justify-center mb-4">
            <Users size={20} className="text-[#AE8F78]" />
          </div>
          <div className="font-serif italic text-3xl text-[#3F5043] leading-tight">
            Soarlife Patient Portal
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mt-1 text-center">
            Roadmap · AI concierge · Groups · Records · Messages
          </div>
        </div>

        <div className="flex gap-2 mb-5 bg-[#EDEDEE] p-1 rounded-full">
          {["login", "register"].map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`community-tab-${m}`}
              onClick={() => setMode(m)}
              className={`flex-1 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-semibold transition-all ${
                mode === m ? "bg-[#3F5043] text-[#F8F7F3]" : "text-[#3F5043]/70"
              }`}
            >
              {m === "login" ? "Sign in" : "Create account"}
            </button>
          ))}
        </div>

        {mode === "register" && (
          <input
            data-testid="community-name"
            required
            placeholder="Full name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="w-full bg-[#EDEDEE] rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/50 focus:outline-none focus:ring-2 focus:ring-[#899480] mb-3"
          />
        )}
        <input
          data-testid="community-email"
          required
          type="email"
          placeholder="Email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full bg-[#EDEDEE] rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/50 focus:outline-none focus:ring-2 focus:ring-[#899480] mb-3"
        />
        <input
          data-testid="community-password"
          required
          minLength={mode === "register" ? 8 : 1}
          type="password"
          placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"}
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="w-full bg-[#EDEDEE] rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/50 focus:outline-none focus:ring-2 focus:ring-[#899480]"
        />
        {err && (
          <div
            data-testid="community-auth-error"
            className="mt-3 text-sm text-[#875F45] bg-[#875F45]/10 border border-[#875F45]/30 rounded-xl px-3 py-2"
          >
            {err}
          </div>
        )}
        <button
          type="submit"
          data-testid="community-auth-submit"
          disabled={loading}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors disabled:opacity-60"
        >
          {loading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : mode === "login" ? (
            "Sign in"
          ) : (
            "Create account"
          )}
        </button>

        <div className="my-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-[#BCB3A6]/60" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#899480] font-semibold">
            or
          </span>
          <div className="h-px flex-1 bg-[#BCB3A6]/60" />
        </div>

        <button
          type="button"
          data-testid="community-google-signin"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/community";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="w-full inline-flex items-center justify-center gap-3 rounded-full px-6 py-3 bg-[#F8F7F3] border border-[#BCB3A6] text-[#3F5043] text-sm font-medium hover:border-[#3F5043] transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C33.6 6.1 29 4 24 4c-7.3 0-13.6 4.1-16.7 10.7z"/>
            <path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-6-5.1c-2 1.4-4.4 2.1-6.9 2.1-5.3 0-9.7-3.4-11.3-8l-6.6 5.1C9.4 39.9 16.1 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4.1-3.8 5.5l6 5.1C40 35.5 44 29.9 44 24c0-1.3-.1-2.3-.4-3.5z"/>
          </svg>
          Continue with Google
        </button>
        <div className="mt-4 flex items-center justify-between text-xs">
          <Link to="/" className="text-[#899480] hover:text-[#3F5043]">
            ← Back to Soarlife
          </Link>
          <span className="text-[#899480] inline-flex items-center gap-1">
            <Lock size={10} /> Private & encrypted
          </span>
        </div>
      </form>
    </div>
  );
}

function CommunityHeader({ client, onLogout, backTo, authHeader }) {
  return (
    <header className="bg-[#F8F7F3]/95 backdrop-blur-xl border-b border-[#BCB3A6]/40 sticky top-0 z-10">
      <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
        <Link
          to={backTo || "/"}
          className="inline-flex items-center gap-2 text-sm text-[#3F5043]/80 hover:text-[#3F5043]"
          data-testid="community-back"
        >
          <ArrowLeft size={16} /> {backTo ? "Groups" : "Soarlife"}
        </Link>
        <div className="flex items-center gap-2 text-xs">
          {authHeader && <NotificationBell authHeader={authHeader} basePath="/notifications" />}
          <span className="text-[#3F5043]/70">Hi, {client.full_name.split(" ")[0]}</span>
          <button
            onClick={onLogout}
            data-testid="community-logout"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#EDEDEE] text-[#3F5043] hover:bg-[#BCB3A6]/40"
          >
            <LogOut size={11} /> Sign out
          </button>
        </div>
      </div>
    </header>
  );
}

function fmtSize(bytes) {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AppointmentsSection({ authHeader }) {
  const [appts, setAppts] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/appointments`, {
          headers: authHeader(),
          withCredentials: true,
        });
        setAppts(res.data);
      } catch {
        setAppts([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (appts === null) return null;

  return (
    <section id="appointments" data-testid="appointments-section" className="mt-12">
      <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">
        Your Appointments
      </div>
      {appts.length === 0 ? (
        <div className="bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-3xl p-6 text-sm text-[#3F5043]/60">
          No appointments scheduled yet — your nurse will add them here.
        </div>
      ) : (
        <div className="space-y-2">
          {appts.map((a) => (
            <div
              key={a.id}
              data-testid={`appointment-${a.id}`}
              className="bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-2xl px-5 py-4"
            >
              <div className="flex items-center gap-2 text-[#3F5043] flex-wrap">
                <CalendarDays size={15} className="text-[#875F45] flex-shrink-0" />
                <span className="text-sm font-medium">{a.title}</span>
                <span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded-full bg-[#899480]/20 text-[#3F5043]/70">
                  {{ consult: "Consult", phone: "Phone", paid: "Paid", "follow-up": "Follow-up" }[a.type] || "Consult"}
                </span>
              </div>
              <div className="text-xs text-[#899480] mt-1 ml-6">
                {fmtTime(a.date)}
                {a.location ? ` · ${a.location}` : ""}
              </div>
              {a.notes && (
                <div className="text-xs text-[#3F5043]/70 mt-1.5 ml-6 leading-relaxed">
                  {a.notes}
                </div>
              )}
              {(a.is_video || a.service === "video-consult") && (
                <Link
                  to={`/video-visit/${a.id}${a.manage_token ? `?token=${a.manage_token}` : ""}`}
                  data-testid={`appointment-join-video-${a.id}`}
                  className="mt-3 ml-6 inline-flex items-center gap-2 rounded-full px-5 py-2 bg-[#D4AF37] text-[#3F5043] text-xs font-semibold hover:bg-[#3F5043] hover:text-[#F8F7F3] transition-colors"
                >
                  Join video visit
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function CareChatSection({ client, authHeader }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/dm`, {
        headers: authHeader(),
        withCredentials: true,
      });
      setMessages(res.data);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const content = input.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/dm`,
        { content },
        { headers: authHeader(), withCredentials: true }
      );
      setInput("");
      await load();
    } finally {
      setSending(false);
    }
  };

  const sendVoice = async (blob, ext, duration) => {
    const fd = new FormData();
    fd.append("file", blob, `voice.${ext}`);
    fd.append("duration", String(duration));
    await axios.post(`${API}/dm/voice`, fd, {
      headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      withCredentials: true,
    });
    await load();
  };

  return (
    <section id="care-messages" data-testid="care-chat-section" className="mt-12">
      <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">
        Messages with your care team
      </div>
      <div className="bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-3xl overflow-hidden">
        <div ref={scrollRef} className="h-72 overflow-y-auto p-5 space-y-3">
          {messages.length === 0 ? (
            <div className="text-center text-[#899480] text-sm py-10">
              Private line to your Soarlife nurse — ask anything, anytime.
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.author_role === "client";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div>
                    <div
                      className={`text-[10px] uppercase tracking-[0.15em] font-semibold mb-1 ${
                        mine ? "text-right text-[#899480]" : "text-[#875F45]"
                      }`}
                    >
                      {m.author_name}
                      <span className="font-normal normal-case tracking-normal text-[#899480]/70 ml-2">
                        {fmtTime(m.created_at)}
                      </span>
                    </div>
                    {m.kind === "voice" ? (
                      <VoiceMessagePlayer messageId={m.id} duration={m.duration} authHeader={authHeader} mine={mine} />
                    ) : (
                    <div
                      className={`inline-block px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        mine
                          ? "bg-[#3F5043] text-[#F8F7F3] rounded-br-sm"
                          : "bg-[#875F45]/15 text-[#3F5043] border border-[#875F45]/30 rounded-bl-sm"
                      }`}
                    >
                      {m.content}
                    </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <form onSubmit={send} className="border-t border-[#BCB3A6]/40 p-3 flex items-end gap-2">
          <textarea
            data-testid="care-chat-input"
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Message your nurse…"
            className="flex-1 resize-none bg-[#EDEDEE] rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/50 focus:outline-none focus:ring-2 focus:ring-[#899480] max-h-32"
          />
          <VoiceRecorderButton onSend={sendVoice} disabled={sending} testid="care-chat-voice-btn" />
          <button
            type="submit"
            data-testid="care-chat-send"
            disabled={sending || !input.trim()}
            className="w-11 h-11 rounded-full bg-[#3F5043] text-[#F8F7F3] flex items-center justify-center hover:bg-[#875F45] transition-colors disabled:opacity-50 flex-shrink-0"
            aria-label="Send"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </form>
      </div>
    </section>
  );
}

function MedicationsSection({ authHeader }) {
  const [meds, setMeds] = useState([]);
  const [today, setToday] = useState(null);
  const [form, setForm] = useState({ name: "", dose: "", time1: "08:00", time2: "", notes: "" });
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [m, t] = await Promise.all([
        axios.get(`${API}/medications`, { headers: authHeader() }),
        axios.get(`${API}/medications/today`, { headers: authHeader() }),
      ]);
      setMeds(m.data || []);
      setToday(t.data || null);
    } catch {}
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    setErr("");
    try {
      await axios.post(
        `${API}/medications`,
        {
          name: form.name,
          dose: form.dose,
          times: [form.time1, form.time2].filter(Boolean),
          notes: form.notes,
        },
        { headers: authHeader() }
      );
      setForm({ name: "", dose: "", time1: "08:00", time2: "", notes: "" });
      load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not save. Please check the details.");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/medications/${id}`, { headers: authHeader() });
    load();
  };

  const markTaken = async (medId, time) => {
    if (!today) return;
    await axios.post(
      `${API}/medications/${medId}/taken`,
      { date: today.date, time },
      { headers: authHeader() }
    );
    load();
  };

  const inputCls =
    "w-full bg-[#F8F7F3] border border-[#BCB3A6]/50 rounded-2xl px-4 py-2.5 text-sm text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#899480]";

  return (
    <section id="my-medications" data-testid="medications-section" className="mt-12">
      <div className="flex items-center gap-2 mb-1">
        <Pill size={16} className="text-[#875F45]" />
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#875F45] font-semibold">
          Medication reminders
        </div>
      </div>
      <h3 className="font-serif text-2xl text-[#3F5043] mb-5">My medications</h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Today's doses */}
        <div className="bg-white rounded-3xl border border-[#BCB3A6]/40 p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-[#899480] font-semibold mb-4">
            Today's schedule
          </div>
          <div data-testid="med-today-list" className="space-y-2">
            {today && today.doses.length === 0 && (
              <div className="text-sm text-[#3F5043]/60 py-6 text-center">
                No medications scheduled yet — add one on the right.
              </div>
            )}
            {today?.doses.map((d, i) => (
              <div
                key={`${d.med_id}-${d.time}`}
                className={`flex items-center justify-between gap-3 rounded-2xl px-4 py-3 border ${
                  d.taken ? "bg-[#899480]/10 border-[#899480]/40" : "bg-[#F8F7F3] border-[#BCB3A6]/30"
                }`}
              >
                <div>
                  <div className="text-sm font-medium text-[#3F5043]">
                    {d.name}{d.dose ? <span className="text-[#3F5043]/60"> · {d.dose}</span> : null}
                  </div>
                  <div className="text-xs text-[#899480] flex items-center gap-1 mt-0.5">
                    <Clock size={11} /> {d.time}
                  </div>
                </div>
                {d.taken ? (
                  <span data-testid={`dose-taken-${i}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#3F5043]">
                    <Check size={14} className="text-[#899480]" /> Taken
                  </span>
                ) : (
                  <button
                    onClick={() => markTaken(d.med_id, d.time)}
                    data-testid={`dose-mark-${i}`}
                    className="px-4 py-2 rounded-full bg-[#3F5043] text-[#F8F7F3] text-xs font-medium hover:bg-[#875F45] transition-colors"
                  >
                    Mark taken
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="mt-4 text-[11px] text-[#3F5043]/50 leading-relaxed">
            We email your schedule each morning. Always follow your clinic's exact instructions — this is a companion, not medical advice.
          </p>
        </div>

        {/* Schedules + add form */}
        <div className="bg-white rounded-3xl border border-[#BCB3A6]/40 p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-[#899480] font-semibold mb-4">
            My schedule
          </div>
          <div data-testid="med-list" className="space-y-2 mb-4">
            {meds.map((m, i) => (
              <div key={m.id} data-testid={`med-item-${i}`}
                className="flex items-center justify-between gap-3 rounded-2xl px-4 py-3 bg-[#F8F7F3] border border-[#BCB3A6]/30">
                <div>
                  <div className="text-sm font-medium text-[#3F5043]">
                    {m.name}{m.dose ? <span className="text-[#3F5043]/60"> · {m.dose}</span> : null}
                  </div>
                  <div className="flex gap-1.5 mt-1">
                    {m.times.map((t) => (
                      <span key={t} className="text-[10px] font-semibold bg-[#3F5043]/10 text-[#3F5043] px-2 py-0.5 rounded-full">{t}</span>
                    ))}
                  </div>
                </div>
                <button onClick={() => remove(m.id)} data-testid={`med-delete-${i}`}
                  className="text-[#875F45]/60 hover:text-[#875F45] p-1.5">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>

          <form onSubmit={add} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input required data-testid="med-name-input" placeholder="Medication (e.g. Gonal-F)" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              <input data-testid="med-dose-input" placeholder="Dose (e.g. 150 IU)" value={form.dose}
                onChange={(e) => setForm({ ...form, dose: e.target.value })} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.15em] text-[#899480] font-semibold mb-1">Time 1</label>
                <input type="time" required data-testid="med-time1-input" value={form.time1}
                  onChange={(e) => setForm({ ...form, time1: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.15em] text-[#899480] font-semibold mb-1">Time 2 (optional)</label>
                <input type="time" data-testid="med-time2-input" value={form.time2}
                  onChange={(e) => setForm({ ...form, time2: e.target.value })} className={inputCls} />
              </div>
            </div>
            {err && (
              <div data-testid="med-add-error" className="text-xs text-[#875F45] bg-[#875F45]/10 border border-[#875F45]/30 rounded-xl px-3 py-2">{err}</div>
            )}
            <button type="submit" disabled={adding} data-testid="med-add-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 bg-[#875F45] text-[#F8F7F3] text-sm font-medium hover:bg-[#3F5043] transition-colors disabled:opacity-60">
              {adding ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} /> Add to my schedule</>}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function RecordsSection({ authHeader }) {
  const [records, setRecords] = useState([]);
  const [label, setLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/records`, {
        headers: authHeader(),
        withCredentials: true,
      });
      setRecords(res.data);
    } catch {}
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploading) return;
    setUploading(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label);
      await axios.post(`${API}/records/upload`, fd, {
        headers: authHeader(),
        withCredentials: true,
      });
      setLabel("");
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (r) => {
    try {
      const res = await axios.get(`${API}/records/${r.id}/download`, {
        headers: authHeader(),
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const remove = async (r) => {
    try {
      await axios.delete(`${API}/records/${r.id}`, {
        headers: authHeader(),
        withCredentials: true,
      });
      load();
    } catch {}
  };

  return (
    <section id="my-records" data-testid="records-section" className="mt-12">
      <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">
        My Fertility Records
      </div>
      <div className="bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-3xl p-6">
        <p className="text-sm text-[#3F5043]/70 mb-4">
          Securely upload lab results, protocols, or documents (PDF, images, DOC — max 10 MB). Only you and your Soarlife care team can see them.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="record-label-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (e.g. Day-3 labs)"
            className="flex-1 bg-[#EDEDEE] rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/50 focus:outline-none focus:ring-2 focus:ring-[#899480]"
          />
          <label
            data-testid="record-upload-button"
            className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium cursor-pointer transition-colors ${
              uploading
                ? "bg-[#899480] text-[#F8F7F3]"
                : "bg-[#3F5043] text-[#F8F7F3] hover:bg-[#875F45]"
            }`}
          >
            {uploading ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Upload size={15} />
            )}
            {uploading ? "Uploading…" : "Upload document"}
            <input
              ref={fileRef}
              data-testid="record-file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.txt,.csv,.doc,.docx"
              className="hidden"
              onChange={upload}
              disabled={uploading}
            />
          </label>
        </div>
        {err && (
          <div
            data-testid="record-upload-error"
            className="mt-3 text-sm text-[#875F45] bg-[#875F45]/10 border border-[#875F45]/30 rounded-xl px-3 py-2"
          >
            {err}
          </div>
        )}
        <div className="mt-5 space-y-2">
          {records.length === 0 ? (
            <div className="text-sm text-[#899480] py-2" data-testid="records-empty">
              No documents uploaded yet.
            </div>
          ) : (
            records.map((r) => (
              <div
                key={r.id}
                data-testid={`record-item-${r.id}`}
                className="flex items-center justify-between gap-3 bg-[#EDEDEE] rounded-2xl px-4 py-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText size={16} className="text-[#899480] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-[#3F5043] truncate">
                      {r.label || r.original_filename}
                    </div>
                    <div className="text-[11px] text-[#899480]">
                      {r.original_filename} · {fmtSize(r.size)} · {fmtTime(r.created_at)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    data-testid={`record-download-${r.id}`}
                    onClick={() => download(r)}
                    className="p-2 rounded-full text-[#3F5043]/70 hover:bg-[#BCB3A6]/40"
                    aria-label="Download"
                  >
                    <Download size={14} />
                  </button>
                  <button
                    data-testid={`record-delete-${r.id}`}
                    onClick={() => remove(r)}
                    className="p-2 rounded-full text-[#875F45]/70 hover:bg-[#875F45]/10"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}

const IR_TEXT = {
  en: {
    title: "Request in-person support",
    desc: "A Soarlife nurse or interpreter meets you at your appointment (Orlando area, $80 — locations farther out may adjust). Choose language interpretation or Attend With Me companionship. Send your request, and once Soarlife accepts, you'll pay here to confirm.",
    typeInterpreter: "Interpreter",
    typeAttend: "Attend With Me",
    code: "Support code (IVF clients — $20/day)",
    clinic: "Which clinic? (e.g., Orlando Fertility Institute)",
    address: "Appointment address",
    phone: "Your phone (for text updates)",
    notes: "Notes (optional)",
    submit: "Send request",
    success: "Request sent! Soarlife will review availability and accept it shortly — then you'll pay right here to confirm.",
    error: "Could not send your request — please try again.",
    pay: "to confirm",
    cancel: "Cancel",
    interpreterWord: "support",
    codeApplied: "IVF code applied — $20 admin fee",
  },
  fr: {
    title: "Demander un accompagnement en personne",
    desc: "Une infirmière ou un interprète Soarlife vous accompagne à votre rendez-vous (région d'Orlando, 80 $ — les lieux plus éloignés peuvent être ajustés). Choisissez l'interprétation ou l'accompagnement Attend With Me. Envoyez votre demande, et une fois acceptée, vous paierez ici pour confirmer.",
    typeInterpreter: "Interprète",
    typeAttend: "Attend With Me",
    code: "Code de soutien (clientes FIV — 20 $/jour)",
    clinic: "Quelle clinique ? (ex. : Orlando Fertility Institute)",
    address: "Adresse du rendez-vous",
    phone: "Votre téléphone (pour les nouvelles par texto)",
    notes: "Notes (facultatif)",
    submit: "Envoyer la demande",
    success: "Demande envoyée ! Soarlife va vérifier les disponibilités et l'accepter sous peu — vous paierez ensuite ici pour confirmer.",
    error: "Impossible d'envoyer votre demande — veuillez réessayer.",
    pay: "pour confirmer",
    cancel: "Annuler",
    interpreterWord: "accompagnement",
    codeApplied: "Code FIV appliqué — frais admin 20 $",
  },
};

const INTERP_STATUS = {
  pending: { en: "Waiting for Soarlife", fr: "En attente de Soarlife", cls: "bg-[#AE8F78]/15 text-[#875F45]" },
  awaiting_payment: { en: "Accepted — payment due", fr: "Acceptée — paiement dû", cls: "bg-[#D4AF37]/20 text-[#875F45]" },
  confirmed: { en: "Confirmed", fr: "Confirmée", cls: "bg-[#899480]/20 text-[#3F5043]" },
  completed: { en: "Completed", fr: "Terminée", cls: "bg-[#3F5043]/10 text-[#3F5043]" },
  declined: { en: "Unavailable", fr: "Non disponible", cls: "bg-red-100 text-red-700" },
  canceled: { en: "Canceled", fr: "Annulée", cls: "bg-red-100 text-red-700" },
};

function InterpreterRequestSection({ authHeader }) {
  const [lang, setLang] = useState("en");
  const tt = IR_TEXT[lang];
  const [form, setForm] = useState({
    language: "ht", date: "", time: "", address: "", clinic_name: "", phone: "", notes: "",
    request_type: "interpreter", code: "",
  });
  const [requests, setRequests] = useState([]);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const res = await axios.get(`${API}/interpreter-requests/mine`, { headers: authHeader() });
      setRequests(res.data || []);
    } catch {}
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setErr("");
    setMsg("");
    try {
      await axios.post(`${API}/interpreter-requests`, form, { headers: authHeader() });
      setMsg(tt.success);
      setForm({ language: "ht", date: "", time: "", address: "", clinic_name: "", phone: "", notes: "", request_type: "interpreter", code: "" });
      load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || tt.error);
    } finally {
      setSending(false);
    }
  };

  const pay = async (id) => {
    try {
      const res = await axios.post(`${API}/interpreter-requests/${id}/checkout`,
        { origin_url: window.location.origin }, { headers: authHeader() });
      window.location.href = res.data.checkout_url;
    } catch (e2) {
      setErr(e2?.response?.data?.detail || tt.error);
    }
  };

  const cancel = async (id) => {
    try {
      await axios.post(`${API}/interpreter-requests/${id}/cancel`, {}, { headers: authHeader() });
      load();
    } catch {}
  };

  const inputCls =
    "w-full bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-2.5 text-sm text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#899480]";
  const today = new Date().toISOString().slice(0, 10);
  const langBtn = (code, label) => (
    <button
      key={code}
      onClick={() => setLang(code)}
      data-testid={`ir-lang-${code}`}
      className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-colors ${
        lang === code ? "bg-[#3F5043] text-[#F8F7F3]" : "text-[#3F5043]/60 hover:text-[#3F5043]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div data-testid="interpreter-request-section" className="mb-8">
      <div className="bg-white rounded-3xl border border-[#BCB3A6]/40 p-6 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Languages size={18} className="text-[#AE8F78]" />
            <h2 className="font-serif text-2xl text-[#3F5043]">{tt.title}</h2>
          </div>
          <div className="inline-flex rounded-full bg-[#F8F7F3] p-1 border border-[#BCB3A6]/40">
            {langBtn("en", "EN")}
            {langBtn("fr", "FR")}
          </div>
        </div>
        <p className="text-sm text-[#3F5043]/70 mb-5 leading-relaxed">{tt.desc}</p>
        <form onSubmit={submit} data-testid="interpreter-request-form" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 flex rounded-2xl bg-[#F8F7F3] border border-[#BCB3A6]/40 p-1" data-testid="ir-type-toggle">
            {[["interpreter", tt.typeInterpreter], ["attend", tt.typeAttend]].map(([val, label]) => (
              <button
                key={val}
                type="button"
                data-testid={`ir-type-${val}`}
                onClick={() => setForm({ ...form, request_type: val })}
                className={`flex-1 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  form.request_type === val ? "bg-[#3F5043] text-[#F8F7F3]" : "text-[#3F5043]/60 hover:text-[#3F5043]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <select data-testid="ir-language" value={form.language}
            onChange={(e) => setForm({ ...form, language: e.target.value })} className={inputCls}>
            <option value="ht">Haitian Creole</option>
            <option value="fr">French</option>
          </select>
          <input data-testid="ir-clinic" required placeholder={tt.clinic}
            value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} className={inputCls} />
          <input data-testid="ir-date" required type="date" min={today}
            value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
          <input data-testid="ir-time" required type="time"
            value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={inputCls} />
          <input data-testid="ir-address" required placeholder={tt.address}
            value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            className={`${inputCls} sm:col-span-2`} />
          <input data-testid="ir-phone" type="tel" placeholder={tt.phone}
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
          <input data-testid="ir-notes" placeholder={tt.notes}
            value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
          <input data-testid="ir-code" placeholder={tt.code}
            value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            className={`${inputCls} sm:col-span-2`} />
          {err && <p className="text-xs text-red-700 sm:col-span-2" data-testid="ir-error">{err}</p>}
          {msg && <p className="text-xs text-[#899480] sm:col-span-2" data-testid="ir-success">{msg}</p>}
          <div className="sm:col-span-2">
            <button type="submit" disabled={sending} data-testid="ir-submit"
              className="rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors disabled:opacity-40 inline-flex items-center gap-2">
              {sending && <Loader2 size={14} className="animate-spin" />}
              {tt.submit}
            </button>
          </div>
        </form>
      </div>

      {requests.length > 0 && (
        <div className="space-y-2" data-testid="ir-list">
          {requests.map((r, i) => {
            const st = INTERP_STATUS[r.status] || INTERP_STATUS.pending;
            return (
              <div key={r.id} data-testid={`ir-row-${i}`}
                className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#3F5043]">
                    {r.request_type === "attend" ? tt.typeAttend : `${r.language === "ht" ? "Haitian Creole" : "French"} ${tt.typeInterpreter}`} — {r.clinic_name}
                  </div>
                  <div className="text-xs text-[#3F5043]/60 mt-0.5">
                    {r.date} at {r.time} · {r.address}
                  </div>
                  {r.code_applied && (
                    <div className="text-[11px] text-[#899480] font-semibold mt-1" data-testid={`ir-code-badge-${i}`}>
                      {tt.codeApplied}
                    </div>
                  )}
                  {r.admin_note && (
                    <div className="text-xs text-[#875F45] mt-1">Soarlife: {r.admin_note}</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 rounded-full ${st.cls}`}
                    data-testid={`ir-status-${i}`}>
                    {st[lang] || st.en}
                  </span>
                  {r.status === "awaiting_payment" && (
                    <button onClick={() => pay(r.id)} data-testid={`ir-pay-${i}`}
                      className="rounded-full px-4 py-2 bg-[#875F45] text-white text-xs font-semibold hover:bg-[#3F5043] transition-colors">
                      Pay ${(r.quoted_amount / 100).toFixed(2)} {tt.pay}
                    </button>
                  )}
                  {["pending", "awaiting_payment"].includes(r.status) && (
                    <button onClick={() => cancel(r.id)} data-testid={`ir-cancel-${i}`}
                      className="text-xs text-[#3F5043]/50 hover:text-red-700">
                      {tt.cancel}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function PortalOverview() {
  const { authHeader } = useClient();
  const [guide, setGuide] = useState(null); // {title, sub, src}
  const [guideLang, setGuideLang] = useState("en");
  const [showInterp, setShowInterp] = useState(false);
  const scrollToRecords = () => {
    const el = document.getElementById("my-records");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const GUIDES = [
    {
      key: "injection",
      icon: Syringe,
      title: "Injection Guide",
      desc: "Storage, preparation & step-by-step injection technique — 11 min.",
      src: "/videos/injection-guide.mp4",
      srcFr: "/videos/injection-guide-fr.mp4",
      sub: "Storage · Preparation · Administration — educational only",
      testid: "portal-quick-injection-guide",
    },
    {
      key: "ivf",
      icon: CalendarDays,
      title: "IVF Timeline",
      desc: "Every phase from first consult to pregnancy test — 5 min.",
      src: "/videos/ivf-timeline.mp4",
      srcFr: "/videos/ivf-timeline-fr.mp4",
      sub: "The six phases of IVF — educational only",
      testid: "portal-quick-ivf-guide",
    },
    {
      key: "tww",
      icon: Clock,
      title: "Two-Week Wait",
      desc: "Caring for yourself between transfer and test day — 4 min.",
      src: "/videos/tww-support.mp4",
      srcFr: "/videos/tww-support-fr.mp4",
      sub: "Symptoms, self-care & support — educational only",
      testid: "portal-quick-tww-guide",
    },
  ];
  return (
    <>
      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <Link
          to="/roadmap"
          data-testid="portal-quick-roadmap"
          className="p-5 rounded-3xl bg-[#115E59] text-[#FFFFF8] hover:bg-[#0D4A46] transition-colors"
        >
          <Map size={18} className="text-[#D4AF37] mb-3" />
          <div className="font-serif text-lg leading-tight mb-1">My Fertility Roadmap™</div>
          <div className="text-xs text-[#FFFFF8]/70 leading-relaxed">
            Timelines, education, checklists, and your readiness score.
          </div>
        </Link>
        <Link
          to="/chat"
          data-testid="portal-quick-ai"
          className="p-5 rounded-3xl bg-[#3F5043] text-[#F8F7F3] hover:bg-[#875F45] transition-colors"
        >
          <Sparkles size={18} className="text-[#AE8F78] mb-3" />
          <div className="font-serif text-lg leading-tight mb-1">Chat with Soarlife AI</div>
          <div className="text-xs text-[#F8F7F3]/70 leading-relaxed">
            Your private concierge — appointment prep, questions, EN &amp; Kreyòl.
          </div>
        </Link>
        <button
          onClick={scrollToRecords}
          data-testid="portal-quick-records"
          className="text-left p-5 rounded-3xl bg-[#F8F7F3] border border-[#BCB3A6]/40 hover:border-[#899480] transition-colors"
        >
          <FileText size={18} className="text-[#899480] mb-3" />
          <div className="font-serif text-lg text-[#3F5043] leading-tight mb-1">My records vault</div>
          <div className="text-xs text-[#3F5043]/70 leading-relaxed">
            Upload lab results and protocols — private and secure.
          </div>
        </button>
        <a
          href="/book"
          data-testid="portal-quick-book"
          className="p-5 rounded-3xl bg-[#F8F7F3] border border-[#BCB3A6]/40 hover:border-[#899480] transition-colors"
        >
          <CalendarDays size={18} className="text-[#899480] mb-3" />
          <div className="font-serif text-lg text-[#3F5043] leading-tight mb-1">Book a visit</div>
          <div className="text-xs text-[#3F5043]/70 leading-relaxed">
            Schedule consultations, support packages, and interpreters.
          </div>
        </a>
        <button
          onClick={() => setShowInterp(!showInterp)}
          data-testid="portal-quick-interpreter"
          className={`text-left p-5 rounded-3xl border transition-colors ${
            showInterp
              ? "bg-[#875F45] text-[#F8F7F3] border-[#875F45]"
              : "bg-[#F8F7F3] border-[#BCB3A6]/40 hover:border-[#875F45]"
          }`}
        >
          <Languages size={18} className={`mb-3 ${showInterp ? "text-[#D4AF37]" : "text-[#875F45]"}`} />
          <div className={`font-serif text-lg leading-tight mb-1 ${showInterp ? "" : "text-[#3F5043]"}`}>
            Request an interpreter
          </div>
          <div className={`text-xs leading-relaxed ${showInterp ? "text-[#F8F7F3]/75" : "text-[#3F5043]/70"}`}>
            In-person Haitian Creole or French interpreter for your appointments.
          </div>
        </button>
        <Link
          to="/learn"
          data-testid="portal-quick-learn"
          className="p-5 rounded-3xl text-[#3F5043] transition-colors hover:bg-[#EDE8F7]"
          style={{ background: "#F3EFFA", border: "1px solid #B7A6D966" }}
        >
          <BookOpen size={18} className="mb-3" style={{ color: "#7C6AAE" }} />
          <div className="font-serif text-lg leading-tight mb-1">Education Center</div>
          <div className="text-xs text-[#3F5043]/70 leading-relaxed">
            Success guide, wellness library, supplements, quiz &amp; journal.
          </div>
        </Link>
        {GUIDES.map((g) => {
          const GIcon = g.icon;
          return (
            <button
              key={g.key}
              onClick={() => { setGuide(g); setGuideLang("en"); }}
              data-testid={g.testid}
              className="text-left p-5 rounded-3xl bg-[#115E59]/90 text-[#FFFFF8] hover:bg-[#0D4A46] transition-colors"
            >
              <GIcon size={18} className="text-[#D4AF37] mb-3" />
              <div className="font-serif text-lg leading-tight mb-1">{g.title} · Video</div>
              <div className="text-xs text-[#FFFFF8]/75 leading-relaxed">{g.desc}</div>
            </button>
          );
        })}
      </div>

      {showInterp && <InterpreterRequestSection authHeader={authHeader} />}

      {/* Video guide modal (with EN/Kreyòl toggle for the injection guide) */}
      {guide && (
        <div
          data-testid="injection-guide-modal"
          className="fixed inset-0 z-50 bg-[#0D4A46]/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setGuide(null)}
        >
          <div
            className="w-full max-w-3xl bg-[#FFFFF8] rounded-3xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 bg-[#115E59]">
              <div>
                <div className="font-serif text-lg text-[#FFFFF8]">{guide.title}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                  {guide.sub}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {guide.srcFr && (
                  <div className="flex rounded-full bg-[#FFFFF8]/10 p-0.5" data-testid="guide-lang-toggle">
                    {[["en", "English"], ["fr", "Français"]].map(([k, label]) => (
                      <button
                        key={k}
                        onClick={() => setGuideLang(k)}
                        data-testid={`guide-lang-${k}`}
                        className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${
                          guideLang === k ? "bg-[#D4AF37] text-[#115E59]" : "text-[#FFFFF8]/70 hover:text-[#FFFFF8]"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setGuide(null)}
                  data-testid="injection-guide-close"
                  className="w-9 h-9 rounded-full bg-[#FFFFF8]/10 text-[#FFFFF8] flex items-center justify-center hover:bg-[#FFFFF8]/20"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            <video
              key={guideLang}
              data-testid="injection-guide-video"
              src={guideLang === "fr" && guide.srcFr ? guide.srcFr : guide.src}
              controls
              playsInline
              preload="metadata"
              className="w-full aspect-video bg-black"
            />
          </div>
        </div>
      )}

      {/* Care team */}
      <div className="bg-[#875F45]/10 border border-[#875F45]/25 rounded-3xl p-6 mb-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#875F45] font-semibold mb-1">
            Your care team
          </div>
          <div className="font-serif text-xl text-[#3F5043] mb-1">
            A nurse is one message away.
          </div>
          <p className="text-sm text-[#3F5043]/70 leading-relaxed max-w-md">
            Questions between appointments? Reach out anytime — we reply within one
            business day.
          </p>
        </div>
        <div className="flex flex-col gap-2 text-sm flex-shrink-0">
          <a
            href="tel:+14073749834"
            data-testid="portal-call-link"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#3F5043] text-[#F8F7F3] hover:bg-[#875F45] transition-colors"
          >
            <Phone size={13} /> Call (407) 374-9834
          </a>
          <a
            href="sms:+18446632125"
            data-testid="portal-text-link"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 border border-[#3F5043]/30 text-[#3F5043] hover:bg-[#3F5043] hover:text-[#F8F7F3] transition-colors"
          >
            <MessageCircle size={13} /> Text (844) 663-2125
          </a>
          <button
            onClick={() => {
              const el = document.getElementById("care-messages");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            data-testid="portal-message-nurse"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 border border-[#875F45]/40 text-[#875F45] hover:bg-[#875F45] hover:text-[#F8F7F3] transition-colors"
          >
            <Send size={13} /> Message your nurse
          </button>
        </div>
      </div>
    </>
  );
}

function GroupList({ client, authHeader, onLogout, onOpen }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/clients/groups`, {
          headers: authHeader(),
          withCredentials: true,
        });
        setGroups(res.data);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="community-groups">
      <CommunityHeader client={client} onLogout={onLogout} authHeader={authHeader} />
      <main className="max-w-4xl mx-auto px-5 py-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">
            Client Portal
          </div>
          <h1 className="font-serif text-4xl text-[#3F5043]">Welcome, {client.full_name.split(" ")[0]}.</h1>
          <p className="mt-3 text-sm text-[#3F5043]/70 max-w-xl leading-relaxed">
            Everything for your journey in one place — your private AI concierge,
            your community groups, and your secure records vault.
          </p>
        </div>

        <PortalOverview />

        <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">
          Your Private Groups
        </div>

        {loading ? (
          <div className="text-center py-10 text-[#899480]">
            <Loader2 className="animate-spin inline" size={20} /> Loading…
          </div>
        ) : groups.length === 0 ? (
          <div className="bg-[#EDEDEE] rounded-3xl p-10 text-center">
            <Users size={30} strokeWidth={1.3} className="mx-auto text-[#899480] mb-3" />
            <div className="font-serif text-2xl text-[#3F5043] mb-2">
              No groups yet.
            </div>
            <p className="text-sm text-[#3F5043]/70 max-w-md mx-auto">
              A Soarlife nurse will add you to a group when one that fits your journey is ready. In the meantime, you can chat with{" "}
              <Link to="/chat" className="text-[#875F45] underline">
                Soarlife AI
              </Link>
              .
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {groups.map((g) => (
              <motion.button
                key={g.id}
                data-testid={`community-group-${g.id}`}
                onClick={() => onOpen(g.id)}
                whileHover={{ y: -2 }}
                className="text-left p-6 rounded-3xl bg-[#F8F7F3] border border-[#BCB3A6]/40 hover:border-[#899480] transition-colors"
              >
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#899480] font-semibold mb-3">
                  <MessageCircle size={12} /> Group
                </div>
                <div className="font-serif text-2xl text-[#3F5043] leading-tight mb-2">
                  {g.name}
                </div>
                {g.description && (
                  <div className="text-sm text-[#3F5043]/70 leading-relaxed">
                    {g.description}
                  </div>
                )}
              </motion.button>
            ))}
          </div>
        )}

        <AppointmentsSection authHeader={authHeader} />
        <CareChatSection client={client} authHeader={authHeader} />

        <RecordsSection authHeader={authHeader} />
        <MedicationsSection authHeader={authHeader} />
        <BillingSection authHeader={authHeader} />
        <AccountSection authHeader={authHeader} onLogout={onLogout} />
      </main>
    </div>
  );
}

function GroupRoom({ groupId, client, authHeader, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [group, setGroup] = useState(null);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await axios.get(`${API}/groups/${groupId}/messages`, {
        headers: authHeader(),
        withCredentials: true,
      });
      setMessages(res.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        navigate("/community");
      }
    }
  };

  useEffect(() => {
    // Also fetch group name
    axios
      .get(`${API}/clients/groups`, { headers: authHeader(), withCredentials: true })
      .then((r) => {
        setGroup(r.data.find((g) => g.id === groupId) || null);
      });
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const content = input.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/groups/${groupId}/messages`,
        { content },
        { headers: authHeader(), withCredentials: true }
      );
      setInput("");
      await load();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3] flex flex-col" data-testid="community-room">
      <CommunityHeader client={client} onLogout={onLogout} backTo="/community" authHeader={authHeader} />
      <div className="bg-[#EDEDEE] border-b border-[#BCB3A6]/30 px-5 py-3">
        <div className="max-w-4xl mx-auto">
          <div className="font-serif text-xl text-[#3F5043]">
            {group?.name || "Group"}
          </div>
          {group?.description && (
            <div className="text-xs text-[#3F5043]/60 mt-0.5">
              {group.description}
            </div>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-5 py-6 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-10 text-[#899480]">
              Say hi to kick off the conversation.
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.author_id === client.id;
              const isAdmin = m.author_role === "admin";
              return (
                <div
                  key={m.id}
                  className={`flex ${mine ? "justify-end" : "justify-start"}`}
                >
                  <div className={`max-w-[80%] ${mine ? "text-right" : ""}`}>
                    <div
                      className={`text-[11px] uppercase tracking-[0.18em] font-semibold mb-1 ${
                        isAdmin ? "text-[#875F45]" : "text-[#899480]"
                      }`}
                    >
                      {isAdmin ? (
                        <span className="inline-flex items-center gap-1">
                          <Sparkles size={10} /> {m.author_name}
                        </span>
                      ) : (
                        m.author_name
                      )}
                      <span className="text-[#899480]/70 font-normal ml-2 lowercase tracking-normal">
                        {fmtTime(m.created_at)}
                      </span>
                    </div>
                    <div
                      className={`inline-block px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        mine
                          ? "bg-[#3F5043] text-[#F8F7F3] rounded-br-sm"
                          : isAdmin
                          ? "bg-[#875F45]/15 text-[#3F5043] border border-[#875F45]/30 rounded-bl-sm"
                          : "bg-[#EDEDEE] text-[#3F5043] rounded-bl-sm"
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="bg-[#F8F7F3] border-t border-[#BCB3A6]/40 sticky bottom-0">
        <div className="max-w-4xl mx-auto px-5 py-3">
          <form onSubmit={send} className="flex items-end gap-2">
            <textarea
              data-testid="community-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder="Share what's on your heart…"
              className="flex-1 resize-none bg-[#EDEDEE] rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/50 focus:outline-none focus:ring-2 focus:ring-[#899480] max-h-32"
            />
            <button
              type="submit"
              data-testid="community-send"
              disabled={sending || !input.trim()}
              className="w-11 h-11 rounded-full bg-[#3F5043] text-[#F8F7F3] flex items-center justify-center hover:bg-[#875F45] transition-colors disabled:opacity-50 flex-shrink-0"
              aria-label="Send"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </form>
          <div className="mt-1.5 text-[10px] text-[#3F5043]/55">
            Please be kind. Shared stories, no medical advice.
          </div>
        </div>
      </div>
    </div>
  );
}

function AccountSection({ authHeader, onLogout }) {
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/clients/me`, { headers: authHeader(), withCredentials: true })
      .then((r) => setMe(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasPassword = me?.auth_provider !== "google";
  const ready = hasPassword ? password.length > 0 : confirmText.trim().toUpperCase() === "DELETE";

  const doDelete = async () => {
    if (busy || !ready) return;
    setBusy(true);
    setErr("");
    try {
      await axios.post(
        `${API}/clients/me/delete`,
        { password, confirm_text: confirmText },
        { headers: authHeader(), withCredentials: true }
      );
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      navigate("/", { state: { accountDeleted: true } });
      window.location.href = "/";
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't delete the account — please try again.");
      setBusy(false);
    }
  };

  return (
    <section id="account" data-testid="account-section" className="mt-12">
      <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">
        Account & privacy
      </div>
      <div className="bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-3xl p-6">
        {!open ? (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-[#3F5043]/70 max-w-lg leading-relaxed">
              You can permanently delete your account and personal data anytime — your login, roadmap,
              messages, medications, and uploaded records are removed. Payment receipts are retained
              for accounting as described in our privacy policy.
            </p>
            <button
              onClick={() => setOpen(true)}
              data-testid="delete-account-open"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-[#875F45]/50 text-[#875F45] text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#875F45]/5 transition-colors"
            >
              <Trash2 size={13} /> Delete my account
            </button>
          </div>
        ) : (
          <div data-testid="delete-account-panel">
            <div className="font-serif text-xl text-[#875F45] mb-2">Delete your account?</div>
            <p className="text-sm text-[#3F5043]/70 mb-4 leading-relaxed">
              This is permanent and cannot be undone. Your care team will no longer have access to your
              portal, and any booked appointments will be anonymized.
            </p>
            {hasPassword ? (
              <>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">
                  Confirm with your password
                </label>
                <input
                  type="password"
                  data-testid="delete-account-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  className="w-full max-w-sm bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-3 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#875F45] mb-3"
                />
              </>
            ) : (
              <>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">
                  Type DELETE to confirm
                </label>
                <input
                  data-testid="delete-account-confirm"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="w-full max-w-sm bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-3 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#875F45] mb-3"
                />
              </>
            )}
            {err && (
              <div data-testid="delete-account-error" className="text-sm text-[#875F45] mb-3">{err}</div>
            )}
            <div className="flex gap-3">
              <button
                onClick={doDelete}
                disabled={!ready || busy}
                data-testid="delete-account-confirm-btn"
                className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#875F45] text-white text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#3F5043] transition-colors disabled:opacity-50"
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                Permanently delete
              </button>
              <button
                onClick={() => { setOpen(false); setErr(""); }}
                data-testid="delete-account-cancel"
                className="text-xs uppercase tracking-[0.15em] text-[#899480] hover:text-[#3F5043]"
              >
                Keep my account
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function BillingSection({ authHeader }) {
  const [data, setData] = useState(null);

  const fmtDate = (s) => {
    if (!s) return "—";
    try {
      const d = new Date(s);
      if (isNaN(d.getTime())) return String(s).slice(0, 10);
      return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch { return String(s).slice(0, 10); }
  };

  useEffect(() => {
    axios.get(`${API}/my/billing`, { headers: authHeader(), withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({ invoices: [], summaries: [], payments: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (path, filename, testid) => {
    try {
      const res = await axios.get(`${API}${path}`, {
        headers: authHeader(), responseType: "blob", withCredentials: true });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {}
  };

  if (!data) return null;
  const { invoices, summaries, payments } = data;
  if (!invoices.length && !summaries.length && !payments.length) return null;

  const STATUS_CLS = {
    draft: "bg-[#EDEDEE] text-[#3F5043]",
    sent: "bg-[#D4AF37]/20 text-[#875F45]",
    unpaid: "bg-[#875F45]/15 text-[#875F45]",
    paid: "bg-[#899480]/15 text-[#3F5043]",
  };

  return (
    <section id="billing" data-testid="billing-section" className="mt-12">
      <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">
        Billing & session summaries
      </div>

      {summaries.length > 0 && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Session summaries</div>
          <div className="space-y-2">
            {summaries.map((n) => (
              <div key={n.id} data-testid={`my-summary-${n.id}`}
                className="flex items-center justify-between gap-3 bg-white border border-[#BCB3A6]/40 rounded-2xl px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-[#3F5043]">
                    Session note{n.sections_date ? ` · ${n.sections_date}` : ""}
                  </div>
                  <div className="text-xs text-[#899480]">
                    {n.consultant_name ? `${n.consultant_name} · ` : ""}{fmtDate(n.finalized_at || n.created_at)}
                  </div>
                </div>
                <button onClick={() => download(`/my/notes/${n.id}/pdf`, `Soarlife_Session_${n.id.slice(0, 8)}.pdf`)}
                  data-testid={`my-summary-pdf-${n.id}`}
                  className="inline-flex items-center gap-1.5 text-xs rounded-full px-4 py-2 border border-[#899480]/50 text-[#3F5043] hover:bg-[#899480]/10 transition-colors">
                  <Download size={12} /> PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {invoices.length > 0 && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Invoices</div>
          <div className="space-y-2">
            {invoices.map((inv) => (
              <div key={inv.id} data-testid={`my-invoice-${inv.id}`}
                className="flex items-center justify-between gap-3 bg-white border border-[#BCB3A6]/40 rounded-2xl px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-[#3F5043] flex items-center gap-2">
                    {inv.invoice_number}
                    <span className={`text-[9px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 rounded-full ${STATUS_CLS[inv.status] || STATUS_CLS.draft}`}>
                      {inv.status}
                    </span>
                  </div>
                  <div className="text-xs text-[#899480]">
                    ${inv.total?.toFixed(2)} · {fmtDate(inv.invoice_date)}{inv.due_date ? ` · due ${fmtDate(inv.due_date)}` : ""}
                  </div>
                </div>
                <button onClick={() => download(`/my/invoices/${inv.id}/pdf`, `${inv.invoice_number}.pdf`)}
                  data-testid={`my-invoice-pdf-${inv.id}`}
                  className="inline-flex items-center gap-1.5 text-xs rounded-full px-4 py-2 border border-[#899480]/50 text-[#3F5043] hover:bg-[#899480]/10 transition-colors">
                  <Download size={12} /> PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {payments.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Payment history</div>
          <div className="space-y-2">
            {payments.map((p, i) => (
              <div key={i} data-testid={`my-payment-${i}`}
                className="flex items-center justify-between gap-3 bg-white border border-[#BCB3A6]/40 rounded-2xl px-4 py-3">
                <div className="text-sm text-[#3F5043]">{p.label || p.kind}</div>
                <div className="text-xs text-[#899480]">${Number(p.amount || 0).toFixed(2)} · {fmtDate(p.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
